import { parseMarkdown, linkHrefs, type Block, type MarkdownProblem } from './markdown';

// Help articles: a markdown file with a small frontmatter header. This module is
// pure (no Vite, no React) so the tests and any script can parse a file the same
// way the apps do. The Vite glob that loads the real files is in ./registry.ts.

export type HelpAudience = 'admin' | 'seller' | 'buyer';
export const HELP_AUDIENCES: readonly HelpAudience[] = ['admin', 'seller', 'buyer'];

export const SUMMARY_MAX = 160;

export interface HelpArticle {
  /** `<audience>/<slug>`, e.g. `admin/invite-a-buyer`. */
  id: string;
  audience: HelpAudience;
  slug: string;
  title: string;
  summary: string;
  category: string;
  order: number;
  /** The markdown after the frontmatter. */
  body: string;
  blocks: Block[];
  /** The `##` headings, in order — the anchors a tip may deep-link to. */
  anchors: { anchor: string; text: string }[];
}

export interface Frontmatter {
  data: Record<string, string>;
  body: string;
  /** Lines of file taken by the header, so body problems can report file lines. */
  bodyStartLine: number;
}

/** Split `---` fenced `key: value` frontmatter from the body. Tolerates CRLF and a BOM. */
export function parseFrontmatter(raw: string): Frontmatter | { error: string } {
  const text = raw.replace(/^﻿/, '').replace(/\r\n?/g, '\n');
  const lines = text.split('\n');
  if (lines[0]?.trim() !== '---') {
    return { error: 'missing frontmatter: the file must start with a "---" line' };
  }
  const end = lines.findIndex((l, i) => i > 0 && l.trim() === '---');
  if (end === -1) return { error: 'frontmatter is not closed: add a closing "---" line' };
  const data: Record<string, string> = {};
  for (let i = 1; i < end; i++) {
    const line = lines[i] ?? '';
    if (line.trim() === '') continue;
    const m = /^([A-Za-z][\w-]*)\s*:\s*(.*)$/.exec(line);
    if (!m) return { error: `frontmatter line ${i + 1} is not "key: value": ${line.trim()}` };
    let value = (m[2] ?? '').trim();
    if (
      value.length >= 2 &&
      ((value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'")))
    ) {
      value = value.slice(1, -1);
    }
    data[m[1] ?? ''] = value;
  }
  return { data, body: lines.slice(end + 1).join('\n'), bodyStartLine: end + 2 };
}

/** Split an article id into audience and slug, or null when it is not one. */
export function splitArticleId(id: string): { audience: HelpAudience; slug: string } | null {
  const m = /^([a-z]+)\/([a-z0-9]+(?:-[a-z0-9]+)*)$/.exec(id);
  if (!m) return null;
  const audience = HELP_AUDIENCES.find((a) => a === m[1]);
  return audience ? { audience, slug: m[2] ?? '' } : null;
}

export type ArticleParse =
  { ok: true; article: HelpArticle; problems: string[] } | { ok: false; problems: string[] };

/**
 * Parse one article. `problems` lists everything wrong with it, each as a
 * sentence the author can act on (the caller prefixes the file). A file with no
 * usable header is `ok: false`; one with only content problems still parses.
 */
export function parseArticle(id: string, raw: string): ArticleParse {
  const problems: string[] = [];
  const idParts = splitArticleId(id);
  if (!idParts) {
    return {
      ok: false,
      problems: [
        `"${id}" is not a valid article id: use <admin|seller|buyer>/<kebab-case-slug>.md`,
      ],
    };
  }
  const fm = parseFrontmatter(raw);
  if ('error' in fm) return { ok: false, problems: [fm.error] };

  const need = (key: string): string => {
    const v = fm.data[key];
    if (v === undefined || v === '') problems.push(`frontmatter "${key}" is required`);
    return v ?? '';
  };
  const title = need('title');
  const summary = need('summary');
  const category = need('category');
  const orderRaw = need('order');
  let order = 0;
  if (orderRaw !== '') {
    if (/^-?\d+$/.test(orderRaw)) order = Number(orderRaw);
    else problems.push(`frontmatter "order" must be a whole number (got "${orderRaw}")`);
  }
  if (summary.length > SUMMARY_MAX) {
    problems.push(
      `frontmatter "summary" is ${summary.length} characters; the limit is ${SUMMARY_MAX}`,
    );
  }

  const parsed = parseMarkdown(fm.body);
  const lineProblem = (p: MarkdownProblem) => `line ${p.line + fm.bodyStartLine - 1}: ${p.message}`;
  problems.push(...parsed.problems.map(lineProblem));
  if (parsed.blocks.length === 0) problems.push('the article has no body');

  const anchors = parsed.blocks.flatMap((b) =>
    b.type === 'h2' ? [{ anchor: b.anchor, text: b.text }] : [],
  );
  const article: HelpArticle = {
    id,
    audience: idParts.audience,
    slug: idParts.slug,
    title,
    summary,
    category,
    order,
    body: fm.body,
    blocks: parsed.blocks,
    anchors,
  };
  return { ok: true, article, problems };
}

/** Where `help/articles/<audience>/<slug>.md` glob keys point: `admin/slug`, or null. */
export function articleIdFromPath(path: string): string | null {
  const m = /(?:^|\/)articles\/([^/]+)\/([^/]+)\.md$/.exec(path.replace(/\\/g, '/'));
  if (!m) return null;
  const slug = m[2] ?? '';
  // `_notes.md` and the like are not articles.
  if (slug.startsWith('_')) return null;
  return `${m[1]}/${slug}`;
}

export interface LoadedArticles {
  articles: HelpArticle[];
  /** Each problem prefixed with the file it came from. */
  problems: string[];
}

/** Parse every file of a glob result (`{ path: rawText }`). Every problem is
 * listed in `problems` (the tests require none). */
export function loadArticles(files: Record<string, string>): LoadedArticles {
  const articles: HelpArticle[] = [];
  const problems: string[] = [];
  const seen = new Map<string, string>();
  for (const [path, raw] of Object.entries(files).sort(([a], [b]) => a.localeCompare(b))) {
    const id = articleIdFromPath(path);
    if (!id) continue;
    const prior = seen.get(id);
    if (prior) problems.push(`${path}: duplicate article id "${id}" (also ${prior})`);
    seen.set(id, path);
    const result = parseArticle(id, raw);
    problems.push(...result.problems.map((p) => `${path}: ${p}`));
    // A content slip (long summary, stray HTML) still shows the article; the tests
    // fail on it. Only a file with no usable title or category is left out.
    const a = result.ok ? result.article : null;
    if (a && a.title && a.category && !prior) articles.push(a);
  }
  return { articles, problems };
}

/** The in-app path of an article, optionally deep-linked to a `##` anchor. */
export function articlePath(id: string, anchor?: string): string {
  return `/help/${id}${anchor ? `#${anchor}` : ''}`;
}

/** Internal `/help/<audience>/<slug>[#anchor]` links in an article: target id + anchor. */
export function internalHelpLinks(article: HelpArticle): { id: string; anchor?: string }[] {
  const out: { id: string; anchor?: string }[] = [];
  for (const href of linkHrefs(article.blocks)) {
    const m = /^\/help\/([^#?]+?)(?:#(.+))?$/.exec(href);
    if (m) out.push({ id: m[1] ?? '', anchor: m[2] });
  }
  return out;
}

/** Categories in the order the help center lists them; others follow alphabetically. */
export const CATEGORY_ORDER = [
  'Getting started',
  'Rooms',
  'Documents',
  'Checklist',
  'Buyers',
  'LOIs and exclusivity',
  'Q&A',
  'Deal tracking',
  'Access',
  'Account and billing',
] as const;

export function compareCategories(a: string, b: string): number {
  const ia = (CATEGORY_ORDER as readonly string[]).indexOf(a);
  const ib = (CATEGORY_ORDER as readonly string[]).indexOf(b);
  if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  return a.localeCompare(b);
}

export function compareArticles(a: HelpArticle, b: HelpArticle): number {
  return a.order - b.order || a.title.localeCompare(b.title);
}

export interface ArticleGroup {
  category: string;
  articles: HelpArticle[];
}

/** An audience's articles grouped by category (categories and articles ordered). */
export function groupArticles(articles: HelpArticle[]): ArticleGroup[] {
  const byCat = new Map<string, HelpArticle[]>();
  for (const a of articles) byCat.set(a.category, [...(byCat.get(a.category) ?? []), a]);
  return [...byCat.entries()]
    .sort(([a], [b]) => compareCategories(a, b))
    .map(([category, list]) => ({ category, articles: [...list].sort(compareArticles) }));
}

/** Articles whose title, summary or body contain every word of `query` (case-insensitive). */
export function searchArticles(articles: HelpArticle[], query: string): HelpArticle[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return articles;
  const scored = articles.flatMap((a) => {
    const title = a.title.toLowerCase();
    const summary = a.summary.toLowerCase();
    const body = a.body.toLowerCase().replace(/[*[\]()]/g, '');
    if (!words.every((w) => title.includes(w) || summary.includes(w) || body.includes(w))) {
      return [];
    }
    // Title hits first, then summary, then body-only.
    const score = words.reduce(
      (s, w) => s + (title.includes(w) ? 4 : 0) + (summary.includes(w) ? 2 : 0),
      0,
    );
    return [{ a, score }];
  });
  return scored.sort((x, y) => y.score - x.score || compareArticles(x.a, y.a)).map((s) => s.a);
}
